/*
 * Palco - COLIBIKE (produto-específico).
 *
 * Contém tudo o que é da alçada do produto Colibike:
 *   - `ColibikeSlice`: shape do state (type de vélo, bikes com buckets,
 *     origin/destination com shops bike-específicos)
 *   - `COLIBIKE_STAGE_SEQUENCE`: ordem dos stages para este produto
 *   - `initialColibikeSlice`: valores iniciais
 *   - Types de suporte (BikeSize, WeightBucket, PalcoBikeEntry)
 *
 * Todo o que aqui vive:
 *   - É acedido pelo hook `useColibike` (nunca por `usePalco`, que é chassis)
 *   - Não deve ser importado por outros produtos (Colisport terá o seu próprio
 *     `palco.colisport.ts`)
 *   - Segue a convenção de naming `palco.<product>.ts`
 *
 * O adaptador `PalcoBikeEntry[] -> Package[]` (para backend) mora aqui também
 * quando a fase de integração começar - decisão bucket-vs-exact-weight
 * registada em memória para ser feita pelo lado do produto.
 */

import type { StageId } from './palco';
import OnewayAgency from '../../models/oneway-agency.model';
import type { Department } from '../../models/department.model';

/* ── Bike entry no shape do palco (buckets), NOT o Package do backend ──── *
 * Type + Motor migraram do slice per-shipment (removido) para PER-BIKE.
 * Consequência: cada bike pode ter cadre/motor diferentes no mesmo envio
 * (ex.: um tandem elétrico + duas bikes normais no mesmo shipment).
 * O valor de envio (shipmentFlags) é derivado por OR das bikes, NÃO
 * persistido - ver useColibike.shipmentFlags. */
export type BikeSize = 'Small' | 'Medium' | 'Big';

export type WeightBucket = '1-5' | '5-10' | '10-15' | '15-20' | '20-30';

export type BikeCadre = 'unVelo' | 'unTandem';

export type BikeMotor = 'nonElec' | 'elec';

export interface PalcoBikeEntry {
  /** Unique id para React key + edits diferenciadores. Gerado por uuidv4. */
  id: string;
  /** Type de cadre. Default 'unVelo' ao adicionar (redução de fricção -
   *  vélo normal é o caso comum). Título dinâmico do card serve de rede
   *  de segurança caso o utilizador tenha tandem/electric e não altere
   *  os defaults - o label muda visivelmente. */
  cadre: BikeCadre | null;
  /** Motorização. Default 'nonElec' ao adicionar (mesma lógica). */
  motor: BikeMotor | null;
  size: BikeSize | null;
  weightBucket: WeightBucket | null;
  /** Valor declarado em EUR (opcional). Alimenta o cálculo do prémio de
   *  seguro no design. Sincronizado para state.steps.assuranceDetail.
   *  packagesAssurance[] na fase de integração. */
  value: number | null;
  /** Preço da GARANTIE (EUR) para este `value`, calculado no ecrã 1 (preview,
   *  debounce+cancel). `null` = sem valor declarado OU o backend falhou/timeout.
   *  NÃO é definitivo: o RECAP RECONFIRMA sobre `value` (ponto de reconfirmação
   *  do palco - o antigo fazia-o no step Assurance, inexistente aqui). Persistir
   *  aqui dá ao preview/oferta o preço; a fonte final é o recap. Ver
   *  [[recap-garantie-reconfirm-blocker]]. Independente de `value`: manter valor
   *  sem preço é um estado válido (nunca cobra garantia a mais - default 0 a
   *  jusante). */
  garantiePrice: number | null;
}

/* ── Slice Colibike do palco ───────────────────────────────────────────── *
 * NOTA IMPORTANTE: NÃO existe mais um campo `type: {cadre, motor}` de
 * envio único. Cada bike em `bikes[]` tem o seu cadre/motor próprios.
 * O valor per-envio (para filtrar shops no A3) é DERIVADO no `useColibike`
 * como `shipmentFlags = { isElectricBike: bikes.some(elec), isTandemBike:
 * bikes.some(tandem) }` - nunca persistido em duplicado. */
export interface ColibikeSlice {
  /** A2 - até 5 bikes com type/motor/size/weight/value cada. */
  bikes: PalcoBikeEntry[];

  /** A3 - shop de origem. `selectedShop` é OnewayAgency (bike-específico). */
  origin: {
    postal: string;
    country: 'FR';
    selectedShop: OnewayAgency | null;
  };

  /** A4 - destino. EXCLUSIVIDADE: trocar de modo REINICIA a escolha (só existe
   *  um destino) - `handleModeChange` limpa a seleção do outro modo + o texto de
   *  busca. (Supersede a preservação dual-side do [[a4-destination-contract]].)
   *
   *  address/addressExtra DELIBERADAMENTE FORA deste slice: a morada completa
   *  do domicílio (rua + complemento) é recolhida no step Contact (mais à
   *  frente, depois do preço). O Destination step só captura a ESCOLHA
   *  postal-cidade (via Department). Ver [[a4-destination-contract]]. */
  destination: {
    /** DOIS MODOS da entrega de destino (mutuamente exclusivos - um só destino).
     *  RONDA 3a (2026-07-27): o 'collecte' (point de collecte do provider) SAIU
     *  daqui - as agências dependem da OFERTA (inexistente no ecrã 3), por isso a
     *  escolha morada-vs-agência passou para os CONTACTOS. Ver
     *  [[destination-fields-investigation]] e [[cascade-invalidation-machine]].
     *   - 'relais'  = point relais (partner NOSSO). `selectedShop` (OnewayAgency)
     *                 carrega morada + name(contactName) = a receiverInfo do
     *                 destino; a ronda dos contactos esconde esses campos (nem
     *                 input nem espelho) e mapeia-os para receiverInfo no record.
     *   - 'adresse' = livraison à une adresse. O utilizador escolhe um Department
     *                 (postal+cidade = ZONA). Alimenta destinationCode/postalCode.
     *                 A rua/complemento + a escolha morada-vs-agência ficam para
     *                 os CONTACTOS. */
    mode: 'relais' | 'adresse';
    country: 'FR';
    /** mode='relais': partner escolhido (Partner→OnewayAgency mapped). Fonte
     *  autoritativa da receiverInfo do destino relais (morada + contactName). */
    selectedShop: OnewayAgency | null;
    /** mode='adresse': suggestion postal-cidade escolhida da lista. NÃO é
     *  texto livre - é uma opção validada devolvida pelo endpoint department.
     *  Alimenta `orderDto.destinationCode + destinationPostalCode` na
     *  integração. Ver [[destination-fields-investigation]] (guardar TODOS os
     *  campos, não só postalCode). */
    selectedDepartment: Department | null;
    /** mode='adresse' SÓ: escolha do destinatário entre morada pessoal e point
     *  de collecte (agência do transportador), decidida no step de CONTACTOS
     *  (RONDA 3a: as agências dependem da OFERTA, saíram do ecrã 3). O TOGGLE só
     *  aparece quando a oferta suporta agências E há agências para a zona (gate
     *  duplo A.3); sem toggle, força 'personal'. Persistido para sobreviver a
     *  navegação. Default 'personal'. Ver [[destination-fields-investigation]]
     *  e o nó `receiverAgency` em [[cascade-invalidation-machine]]. */
    receiverAddressChoice: 'personal' | 'agency';
  };

  /** B3 - aceitação CONSOLIDADA no RecapStep. Cobre: (a) as Conditions
   *  Générales de Vente; (b) a DECLARAÇÃO DE CONFORMIDADE dos dados declarados
   *  no A2 (bike type/motor/size/weight - o que declara corresponde ao que
   *  envia); e (c) a partir de 4b, a declaração de NÃO enviar mercadorias
   *  proibidas - que setará `assuranceDetail.hasOnlyValidPackages = true` (o
   *  checkbox de embalagem SAIU do ecrã 4). Uma só checkbox obrigatória para
   *  avançar ao pagamento. Ver [[project-recap-consolidation-spec]].
   *
   *  Persistida (diferente do wizard antigo, que usava useState local) para
   *  não obrigar o utilizador a re-aceitar sempre que sai do Récap para
   *  editar via "Modifier" e volta - decisão de produto 2026-07-24. */
  cgvAccepted: boolean;

  /** Flag transient: `true` quando o utilizador editou algo em Modifier
   *  que invalidou a oferta selecionada. Setada pela cascata fina (o reset do
   *  nó `offer` em buildColibikeCascadeResets) e limpa pelo OffersLoader na
   *  primeira vez que corre depois. Usada só
   *  para o loader mostrar uma linha de contexto "Recalcul suite à vos
   *  modifications" - o utilizador percebe porque está a passar pelo
   *  loader outra vez. */
  pendingOfferRecompute: boolean;
}

export const initialColibikeSlice: ColibikeSlice = {
  bikes: [],
  origin: {
    postal: '',
    country: 'FR',
    selectedShop: null,
  },
  destination: {
    mode: 'relais',
    country: 'FR',
    selectedShop: null,
    selectedDepartment: null,
    receiverAddressChoice: 'personal',
  },
  cgvAccepted: false,
  pendingOfferRecompute: false,
};

/** Defaults ao adicionar um bike novo. cadre/motor preenchidos (defaults
 *  do caso comum), size/weight/value em branco (obrigatórios em branco =
 *  guard bloqueia). Consumido em useColibike.addBike. */
export const NEW_BIKE_DEFAULTS: Omit<PalcoBikeEntry, 'id'> = {
  cadre: 'unVelo',
  motor: 'nonElec',
  size: null,
  weightBucket: null,
  value: null,
  garantiePrice: null,
};

/** País de ORIGEM default para os payloads backend (offer/record). Configurável
 *  (NÃO hardcoded espalhado) porque vão ser adicionados países. Hoje sempre
 *  'France'. NB: NÃO é usado no ecrã 1 (o endpoint de garantia é value-only). */
export const COLIBIKE_DEFAULT_ORIGIN_COUNTRY = 'France';

/**
 * Peso numérico (kg) que o backend espera, DERIVADO do bucket: SEMPRE o máximo
 * do bucket MENOS 1 (regra do produto, decidida pelo utilizador):
 *   '1-5'→4, '5-10'→9, '10-15'→14, '15-20'→19, '20-30'→29.
 * Usado ao construir o payload do getOffers/record (ecrãs seguintes). O endpoint
 * de GARANTIE do ecrã 1 é value-only e NÃO leva peso, por isso isto ainda não é
 * chamado no ecrã 1 - fica pronto e testado para o ecrã da oferta.
 */
export const bucketToWeight = (bucket: WeightBucket): number => {
  const max = Number(bucket.split('-')[1]);
  return max - 1;
};

/* ── Sequência de stages do Colibike ───────────────────────────────────── *
 * Ordem canónica para goNext/goPrev. Fase 2: A1 ('type') foi FUNDIDO em A2
 * ('quantite'), a sequência passa de 4 para 3 steps user-visible + os
 * técnicos (photo, loaders, done). O stage 'type' continua no StageId
 * union (chassis) por back-compat; o TypeVeloStage órfão poderia teoricamente
 * ser re-adicionado no futuro. Ver [[a1-orphan-backup]]. */
export const COLIBIKE_STAGE_SEQUENCE: StageId[] = [
  // 'photo' (front do flipper da v1) foi REMOVIDO da sequência: a app nunca
  // aterra nele (initialChassisState.stage='quantite'; loadPalcoFromSession
  // reseta terminais→'quantite') e não existe uso vivo de stage==='photo' no
  // novo design (StageMedia + palco são sempre ambos visíveis, sem flip).
  // Mantê-lo como sequence[0] era um bug latente: Retour no primeiro step
  // (QuantiteStep.goPrev) aterrava em 'photo', que renderiza QuantiteStep mas
  // com pills de Fase B. Removê-lo torna sequence[0]='quantite' (o verdadeiro
  // primeiro passo) - crítico para o reset do chassis, que navega para
  // sequence[0]. 'photo' continua no StageId union (chassis) por back-compat.
  'quantite',
  'origine',
  'destination',
  'loader-offers',
  'offer',
  'contact',
  'recap',
  'paiement',
  'loader-pay',
  'done',
];

/* ── Pills user-visible (PRODUTO, não chassis) ─────────────────────────── *
 * Config phase-aware: o conjunto de pills muda quando o utilizador
 * transita da Fase A (colis info) para a Fase B (choose/contact/recap/pay).
 *
 * Chassis-level: `usePalco.pillState(stage)` só sabe derivar 'cur/done/todo'
 * por posição na sequence. Este ficheiro (produto) define QUE pills existem
 * e a QUE stages mapeiam (um pill pode representar múltiplos stages, ex.:
 * 'colisInfo' colapsa quantite+origine+destination). O collapse-aware
 * derivation vive no Hero, que consome a config via `getColibikePillsForStage`.
 *
 * MAPPING:
 *   Fase A pills (3): um por stage user-visible (quantite/origine/destination)
 *   Fase B pills (5): 'Colis info' colapsa Fase A + 4 novos
 *     - Colis info    → quantite, origine, destination (sempre 'done' em B)
 *     - Choisir       → loader-offers, offer            (loader e offer partilham)
 *     - Contact       → contact
 *     - Récapitulatif → recap
 *     - Paiement      → paiement, loader-pay            (loader-pay partilha)
 */

export interface ColibikePill {
  /** Chave estável (para React key + testids). */
  id: string;
  /** i18n key aplicada com `t(labelKey)` no Hero. */
  labelKey: string;
  /** Stages representados por este pill. Se `palco.stage` estiver em
   *  qualquer destes stages → 'cur'; se `palco.stage` estiver ADIANTE de
   *  todos → 'done'; se ATRÁS de todos → 'todo'. */
  stages: StageId[];
}

const PHASE_A_STAGES: readonly StageId[] = ['quantite', 'origine', 'destination'];

export const COLIBIKE_PHASE_A_PILLS: ColibikePill[] = [
  { id: 'quantite',    labelKey: 'hero.pills.typeQtyPoids', stages: ['quantite'] },
  { id: 'origine',     labelKey: 'hero.pills.origin',       stages: ['origine'] },
  { id: 'destination', labelKey: 'hero.pills.destination',  stages: ['destination'] },
];

export const COLIBIKE_PHASE_B_PILLS: ColibikePill[] = [
  { id: 'colisInfo',   labelKey: 'hero.pills.colisInfo',   stages: ['quantite', 'origine', 'destination'] },
  { id: 'chooseOffer', labelKey: 'hero.pills.chooseOffer', stages: ['loader-offers', 'offer'] },
  { id: 'contact',     labelKey: 'hero.pills.contact',     stages: ['contact'] },
  { id: 'recap',       labelKey: 'hero.pills.recap',       stages: ['recap'] },
  { id: 'paiement',    labelKey: 'hero.pills.paiement',    stages: ['paiement', 'loader-pay'] },
];

/**
 * Devolve o conjunto de pills a mostrar para o `currentStage`. Threshold:
 * qualquer stage em `PHASE_A_STAGES` mostra Fase A pills; qualquer outro
 * (incluindo 'photo' inicial e 'done' final) mostra Fase B pills. Isto é
 * intencional - 'done' deve mostrar todos os pills 'done' (progresso
 * completo visível).
 */
export const getColibikePillsForStage = (currentStage: StageId): ColibikePill[] => {
  if ((PHASE_A_STAGES as readonly StageId[]).includes(currentStage)) return COLIBIKE_PHASE_A_PILLS;
  return COLIBIKE_PHASE_B_PILLS;
};
